import { McLeodClassicIntegration } from "@mcleod/core/src/McLeodClassicIntegration";
import { AutogenLayoutEdiRemitAdvProf } from "./autogen/AutogenLayoutEdiRemitAdvProf";
import { ClickEvent } from "@mcleod/components/src/events/ClickEvent";
import { Button, ChangeEvent, Checkbox, DataSourceAction, DataSourceExecutionEvent,DataSourceMode,  Layout, LookupModelSearchEvent, LookupModelSelectionEvent, SlideoutDecorator, Textbox, ValidationResult } from "@mcleod/components";
import { ModelRow, Navigation, StringUtil } from "@mcleod/core";
import { ModelEdiTemplate } from "./models/ModelEdiTemplate";
import { EDITemplateUpdateProps, EDITemplateSlideout } from "./EDITemplateSlideout";
import { EdiCustomTransControl } from "./EdiCustomTransControl";
import { createCustomizationSlideout, handleButtonMasterBatchClick, handleMasterBatchCheck } from "./EdiUtility";
import { CommonDialogs } from "@mcleod/common/src/CommonDialogs";
import { ValidationUtil } from "@mcleod/components/src/databinding/ValidationUtil";
import { ModelEdiremitProfile } from "./models/ModelEdiremitProfile";
import { handleCommIdSelection } from "./DataFusionUtil";


const layoutURL = "lme/datafusion/EdiRemitAdvProf";
export class EdiRemitAdvProf extends AutogenLayoutEdiRemitAdvProf {

    private REMITTANCE_ADVICE = "A";
    private FUNCTIONAL_ACK = "F";
    private OUTBOUND = "O";
    private INBOUND = "I";
    private XML = "X";
    private JSON = "J";
    override async onLoad() {
        this.mainDataSource.doBeforeDataSourcePost = async (): Promise<boolean> => {
            // Check if we're in ADD mode and ask if user wants to add default events
            if (this.mainDataSource?.mode === DataSourceMode.ADD &&
                this.mainDataSource?.activeRow?.get("partner_id") &&
                this.mainDataSource?.activeRow?.get("alt_partner_id") &&
                this.mainDataSource?.activeRow?.get("version") &&
                this.mainDataSource?.activeRow?.get("company_id") &&
                this.mainDataSource?.activeRow?.get("direction")) {

                const validationResults: ValidationResult[] = this.validate(true, true);
                if (!ValidationUtil.checkValidationResults(validationResults)) {
                    ValidationUtil.showFailedValidationDialog(validationResults.filter(result => !result.isValid));
                    return false;
                }

                const partnerId = this.mainDataSource?.activeRow?.get("partner_id");
                const altPartnerId = this.mainDataSource?.activeRow?.get("alt_partner_id");
                const version = this.mainDataSource?.activeRow?.get("version");
                const companyId = this.mainDataSource?.activeRow?.get("company_id");
                const direction = this.mainDataSource?.activeRow?.get("direction");
                const transactionSet = this.mainDataSource?.activeRow?.get("transaction_set", "214");
                if (await this.profileExists(partnerId, altPartnerId, version, companyId, direction, transactionSet)) {
                    CommonDialogs.showError("Partner profile already exists");
                    return false;
                }
            }

            // Return true to allow the save to proceed
            return true;
        };
    }

    buttonOpenClassicOnClick(event: ClickEvent) {
        McLeodClassicIntegration.openClassicScreen("com.tms.client.loadmaster.edi.EntryEdiRemittanceAdviceProfile", "<ext> search id ='" + this.mainDataSource?.activeRow?.get("id") + "'");
    }

    public static navigateTo(recordId: string) {
        const url = layoutURL + `?mode=update&key=${recordId}`;
        Navigation.navigateTo(url, { newTab: true })
    }

    async templateIdOnLookupModelSearch(event: LookupModelSelectionEvent) {
        if (!StringUtil.isEmptyString(event.getValueFromSelectedRow("id"))) {
            const template = await new ModelEdiTemplate().searchSingle({ id: event.getValueFromSelectedRow("id") });
            this.mainDataSource?.activeRow?.set("version", template.get("version"));
            this.mainDataSource?.activeRow?.set("partner_id", template.get("app_sender_id"));
            this.mainDataSource?.activeRow?.set("alt_partner_id", template.get("sender_id"));
            if (template) {
                this.setFuncAckTab(template.get("format_type") !== this.XML && template.get("format_type") !== this.JSON);
            }
        }
    }

    private setFuncAckTab(isVisible: boolean) {
        this.tabFunctionalAcknowledgement.visible = isVisible;
    }

    templateIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        this.templateBeforeLookupModelSearch(event, this.REMITTANCE_ADVICE, this.mainDataSource?.activeRow?.get("direction"));
    }

    private templateBeforeLookupModelSearch(event: LookupModelSearchEvent, type: string, direction: string) {
        event.filter.direction = direction;
        event.filter.transaction_type = type;
    }

    setupButtonOnClick() {
        this.setupOnClick("template_id", (returnedValues) => {
            this.updateFromTemplate(returnedValues);
        });
    }

    private setupOnClick(fieldName: string, callback: (returnedValues: EDITemplateUpdateProps) => void) {
        EDITemplateSlideout.showSlideout({
            templateId: this.mainDataSource?.activeRow?.get(fieldName), doAfterSlideOut:
                (returnedValues: EDITemplateUpdateProps) => {
                    callback(returnedValues);
                }
        });
    }

    updateFromTemplate(updatedValues: EDITemplateUpdateProps) {
        const lookupModelRow = new ModelRow(this.textboxTemplateId.lookupModel || "lme/datafusion/edi-template", false, {
            id: this.mainDataSource?.activeRow?.get("template_id"),
            name: updatedValues.templateDescription
        });
        this.mainDataSource?.activeRow?.setLookupModelData("template_id", lookupModelRow);
        this.textboxTemplateId.displayData(this.mainDataSource?.activeRow, null, 0)
        this.mainDataSource?.activeRow?.set("version", updatedValues.version);
        this.mainDataSource?.activeRow?.set("transaction_set", updatedValues.transactionSet);
        if (this.mainDataSource?.activeRow?.get("direction") === this.INBOUND) {
            this.mainDataSource?.activeRow?.set("partner_id", updatedValues.appSenderId);
            this.mainDataSource?.activeRow?.set("alt_partner_id", updatedValues.senderId);
        } else {
            this.mainDataSource?.activeRow?.set("partner_id", updatedValues.appReceiverId);
            this.mainDataSource?.activeRow?.set("alt_partner_id", updatedValues.receiverId);
        }
    }

    communicationsIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        this.commBeforeLookupModelSearch(event, this.REMITTANCE_ADVICE);
    }

    private commBeforeLookupModelSearch(event: LookupModelSearchEvent, type: string) {
        event.filter.direction_input = this.mainDataSource?.activeRow?.get("direction");
        event.filter.type = type;
    }

    async funcAckTemplateIdOnLookupModelSearch(event: LookupModelSelectionEvent) {
        if (!StringUtil.isEmptyString(event.getValueFromSelectedRow("id"))) {
            const template = await new ModelEdiTemplate().searchSingle({ id: event.getValueFromSelectedRow("id") });
            this.mainDataSource?.activeRow?.set("func_version", template.get("version"));
            this.mainDataSource?.activeRow?.set("func_partner_id", template.get("app_receiver_id"));
            this.mainDataSource?.activeRow?.set("func_altpartner_id", template.get("receiver_id"));
            if (template) {
                this.setFuncAckTab(template.get("format_type") !== this.XML && template.get("format_type") !== this.JSON);
            }
        }
    }

    async afterExecution(event: DataSourceExecutionEvent) {
        this.tabsetRemitProfile.visible = this.mainDataSource?.mode !== DataSourceMode.ADD;
        // await this.setQuickInfos();
        this.buttonAckTemplateSetup.enabled = !StringUtil.isEmptyString(this.mainDataSource?.activeRow?.get("func_ack_tid"));
        await this.loadTemplateData();
        this.updateUIVisibility();
        this.setNextBatchFields();
        if (event && event.getAction() === DataSourceAction.UPDATE && this.mainDataSource?.activeRow?.get("template_id")) {
            this.buttonSetup.enabled = true;
        }
        this.setFuncAckTab(this.mainDataSource?.activeRow?.getString("edi_template.format_type") !== this.XML && this.mainDataSource?.activeRow?.getString("edi_template.format_type") !== this.JSON);
    }

    private async loadTemplateData() {
        await Promise.all([
            this.sourceEdiTemplate.search({ id: this.mainDataSource?.activeRow?.get("template_id") }),
            this.sourceEdiFuncAckTemplate.search({ id: this.mainDataSource?.activeRow?.get("func_ack_tid") })
        ]);
    }

    private updateUIVisibility() {
        const direction = this.mainDataSource?.activeRow?.get("direction");
        const isInbound = direction === this.INBOUND;

        this.layoutNotifications.tableEdiNotifyInbound.visible = isInbound;
        this.layoutNotifications.tableEdiNotifyOutbound.visible = !isInbound;
        this.tabFunctionalAcknowledgement.visible = isInbound;
        this.layoutEdiApiUsers.setValues(this.REMITTANCE_ADVICE, this.mainDataSource?.activeRow?.get("id"));
    }
    beforeExecution(event: DataSourceExecutionEvent) {
        if (this.mainDataSource?.activeRow?.get("direction") === this.INBOUND && !StringUtil.isEmptyString(this.mainDataSource?.activeRow?.get("edi_customer_id"))) {
            this.mainDataSource?.activeRow?.set("edi_customer", {
                edi_customer_id: this.mainDataSource?.activeRow?.get("edi_customer_id"),
                edi_customer_name: this.mainDataSource?.activeRow?.get("edi_customer_name"),
                edi_customer_scac: this.mainDataSource?.activeRow?.get("edi_customer_scac"),
                edi_carrier: this.mainDataSource?.activeRow?.get("edi_carrier")
            });
        }
    }

    funcAckTemplateIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        this.templateBeforeLookupModelSearch(event, this.FUNCTIONAL_ACK, this.OUTBOUND);
    }

    setupFuncAckButtonOnClick() {
        this.setupOnClick("func_ack_tid", (returnedValues) => {
            this.updateFuncAckFromTemplate(returnedValues);
        });
    }

    updateFuncAckFromTemplate(updatedValues: EDITemplateUpdateProps) {
        const lookupModelRow = new ModelRow(this.textboxFuncAckTid.lookupModel || "lme/datafusion/edi-template", false, {
            id: this.mainDataSource?.activeRow?.get("func_ack_tid"),
            name: updatedValues.templateDescription
        });
        this.mainDataSource?.activeRow?.setLookupModelData("func_ack_tid", lookupModelRow);
        this.textboxFuncAckTid.displayData(this.mainDataSource?.activeRow, null, 0)
        this.mainDataSource?.activeRow?.set("func_version", updatedValues.version);
        if (this.mainDataSource?.activeRow?.get("direction") === this.OUTBOUND) {
            this.mainDataSource?.activeRow?.set("func_partner_id", updatedValues.appSenderId);
            this.mainDataSource?.activeRow?.set("func_altpartner_id", updatedValues.senderId);
        } else {
            this.mainDataSource?.activeRow?.set("func_partner_id", updatedValues.appReceiverId);
            this.mainDataSource?.activeRow?.set("func_altpartner_id", updatedValues.receiverId);
        }
    }


    funcAckCommunicationsIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        this.commBeforeLookupModelSearch(event, this.FUNCTIONAL_ACK);
    }

    /** This is an event handler for the onClick event of buttonFilenameCustomize. */
    buttonFilenameCustomizeOnClick(event: ClickEvent) {
        createCustomizationSlideout(
            "lme/datafusion/EdiCustomFilename",
            "Customize Functional Acknowledgement Filename",
            this.mainDataSource,
            "func_ack_filename",
            "fileMask",
            false,
            this.mainDataSource?.activeRow?.get("func_next_batch") ?? 0
        );
    }


    /** This is an event handler for the onClick event of buttonTransactionCustomize. */
    buttonTransactionCustomizeOnClick(event: ClickEvent) {
        const layout = Layout.getLayout("lme/datafusion/EdiCustomTransControl") as EdiCustomTransControl;

        const generateButton = new Button({
            caption: "Save & Close",
            rowBreak: false,
            color: "primary.reverse",
            backgroundColor: "primary",
            onClick: async (event: ClickEvent) => {
                const mask = layout.generateMask();
                if (layout.validateForbiddenCharacters()) {
                    this.mainDataSource.activeRow?.set("func_ack_st02_frmt", mask);
                    layout.slideOut();
                }
            }
        });
        const sod = new SlideoutDecorator({
            layout,
            title: "Customize Functional Acknowledgement Transmission Control Number",
            width: '45%',
            fillVerticalSpace: true,
            addlComponents: [generateButton],
            headerProps: {

            },
            doAfterSlideIn: async () => {
                layout.mask = this.mainDataSource?.activeRow?.get("func_ack_st02_frmt");
            }

        });
        sod.slideIn({ speed: 200 });
    }

    async masterBatchCheckboxOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange) {
            const check = event.target as Checkbox;
            const partnerName = this.mainDataSource.activeRow.get("partner_name");
            const partnerID = this.mainDataSource.activeRow.get("partner_id");
            const altPartnerId = this.mainDataSource.activeRow.get("alt_partner_id");
            const version = this.mainDataSource.activeRow.get("version");

            await handleMasterBatchCheck(check, partnerName, partnerID, altPartnerId, version);
        }
    }


    private setNextBatchFields() {
        if (!this.checkboxFuncUseMBatch.checked) {
            this.textboxFuncNextBatch.visible = true;
            this.labelNextBatchNumber.visible = false;
            this.labelFuncMasterBatch.visible = false;
        } else {
            this.textboxFuncNextBatch.visible = false;
            this.labelNextBatchNumber.visible = true;
            this.labelFuncMasterBatch.visible = true;
        }
    }

    /** This is an event handler for the onClick event of buttonMasterBatch. */
    async buttonMasterBatchOnClick(event: ClickEvent) {
        const partnerName = this.mainDataSource.activeRow.get("partner_name");
        const partnerID = this.mainDataSource.activeRow.get("partner_id");
        const altPartnerId = this.mainDataSource.activeRow.get("alt_partner_id");
        const version = this.mainDataSource.activeRow.get("version");

        await handleButtonMasterBatchClick(partnerName, partnerID, altPartnerId, version, () => {
            this.checkboxFuncUseMBatch.checked = true;
        });
    }

    /** This is an event handler for the onChange event of textboxTemplateId. */
    textboxTemplateIdOnChange(event: ChangeEvent) {
        this.buttonSetup.enabled = !StringUtil.isEmptyString(this.mainDataSource?.activeRow?.get("template_id"));
    }

    /** This is an event handler for the onChange event of textboxFuncAckTid. */
    textboxFuncAckTidOnChange(event: ChangeEvent) {
        this.buttonAckTemplateSetup.enabled = !StringUtil.isEmptyString(this.mainDataSource?.activeRow?.get("func_ack_tid"));
    }

    setTitle() {
        this.dataHeader.title = (this.mainDataSource?.activeRow?.get("direction") === this.INBOUND ? "Inbound " : "Outbound ") + "Remittance Advice Profile";
    }

    doAfterModeChange() {
        this.setTitle();
    }

    doBeforeModeChange() {
        this.setTitle();
    }

    async profileExists(profileId: string, altPartnerId: string, version: string, companyId: string, direction: string, transactionSet: string): Promise<boolean> {
        const profile = await new ModelEdiremitProfile().searchSingle({
            partner_id: profileId,
            alt_partner_id: altPartnerId,
            version: version,
            company_id: companyId,
            direction: direction,
            transaction_set: transactionSet
        });
        return profile !== null && profile !== undefined;
    }

    /** This is an event handler for the onLookupModelSelection event of textboxFuncCommId. */
    textboxFuncCommIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const textbox = event.target as Textbox;
        const field = textbox.field;
        handleCommIdSelection(field, textbox, event.hasSelection(), this.mainDataSource?.activeRow, "997");
    }
}
